import React from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  LinearProgress,
  Avatar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import InventoryIcon from "@mui/icons-material/Inventory";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { TYPE_ICONS } from "../constants";
import { calculateStock, pluralizeUnit } from "../utils";

const ProductListView = ({
  products,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onTransfer,
  currentTab,
  selectedTechnician,
  selectedWarehouse,
}) => {
  if (products.length === 0) {
    const emptyMessages = {
      0: selectedWarehouse
        ? `No hay artículos en ${selectedWarehouse.name}`
        : "Selecciona un almacén para ver su inventario",
      1: selectedTechnician
        ? `${selectedTechnician.name} no tiene artículos asignados`
        : "Selecciona un técnico para ver su inventario",
      2: "No hay residuos peligrosos registrados",
    };

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          py: 8,
        }}
      >
        <InventoryIcon sx={{ fontSize: 120, color: "grey.300", mb: 3 }} />
        <Typography variant="h5" color="text.secondary" fontWeight={700} mb={1}>
          {emptyMessages[currentTab] || "No hay artículos disponibles"}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {currentTab === 0 && "Usa los filtros o crea un nuevo artículo para comenzar"}
          {currentTab === 1 && "Realiza transferencias desde el almacén principal"}
          {currentTab === 2 && "Los residuos aparecerán aquí cuando se registren"}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.5}>
      {products.map((product) => {
        let stock;

        // ---------- STOCK SEGÚN TAB ----------
        if (currentTab === 0 && product.selectedLocationId) {
          const warehouseData = product.warehouses?.find(
            (w) =>
              String(w.locationId) === String(product.selectedLocationId)
          );

          const warehouseStock = Number(warehouseData?.stock) || 0;

          stock = {
            total: warehouseStock,
            warehouse: warehouseStock,
            technicians: 0,
            warehousePct: warehouseStock > 0 ? 100 : 0,
          };
        } else if (currentTab === 1 && selectedTechnician) {
          const techData = product.technicians?.find(
            (t) => String(t.technicianId) === String(selectedTechnician._id)
          );

          const techStock = Number(techData?.stock) || 0;

          stock = {
            total: techStock,
            warehouse: techStock,
            technicians: 0,
            warehousePct: techStock > 0 ? 100 : 0,
          };
        } else if (currentTab === 2) {
          const hazardousStock = Number(product.hazardousStock) || 0;

          stock = {
            total: hazardousStock,
            warehouse: hazardousStock,
            technicians: 0,
            warehousePct: hazardousStock > 0 ? 100 : 0,
          };
        } else {
          stock = calculateStock(product);
        }

        const { total, warehouse, warehousePct } = stock;
        const isSelected = selected?._id === product._id;

        // ---------- BAJO STOCK ----------
        let isLowStock = false;

        if (currentTab === 0 && product.selectedLocationId) {
          const warehouseData = product.warehouses?.find(
            (w) =>
              String(w.locationId) === String(product.selectedLocationId)
          );
          const min = Number(warehouseData?.minStock) || 0;
          isLowStock = min > 0 && warehouse < min;
        } else if (currentTab === 1 && selectedTechnician) {
          const techData = product.technicians?.find(
            (t) => String(t.technicianId) === String(selectedTechnician._id)
          );
          const min = Number(techData?.minStock) || 0;
          isLowStock = min > 0 && warehouse < min;
        } else {
          const min = Number(product.minStock) || 0;
          isLowStock = min > 0 && warehouse < min;
        }

        const stockStatus = isLowStock ? "error" : "success";

        return (
          <Paper
            key={product._id}
            onClick={() => onSelect(product)}
            sx={{
              p: 2.5,
              cursor: "pointer",
              border: "1px solid",
              borderColor: isSelected ? "primary.main" : "divider",
              bgcolor: isSelected ? "primary.50" : "white",
              borderRadius: 2,
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: isSelected ? "primary.50" : "grey.50",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              },
            }}
          >
            <Stack direction="row" spacing={3} alignItems="center">
              {/* ICONO */}
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: isSelected ? "primary.main" : "grey.200",
                  color: isSelected ? "white" : "grey.600",
                }}
              >
                {TYPE_ICONS[product.type] || <InventoryIcon />}
              </Avatar>

              {/* INFO */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                  <Typography variant="h6" fontWeight={700} fontSize="1rem">
                    {product.name}
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={product.sku} size="small" />
                    {product.brand && (
                      <Chip label={product.brand} size="small" variant="outlined" />
                    )}
                    <Chip
                      label={product.category || "Sin categoría"}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Stack>
                </Stack>

                {/* STOCK */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box sx={{ width: 300 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {currentTab === 0
                          ? "Stock en almacén"
                          : currentTab === 1
                          ? "Stock del técnico"
                          : "Stock en residuos peligrosos"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={`${stockStatus}.main`}
                      >
                        {warehouse} {pluralizeUnit(product.unit, warehouse)}
                      </Typography>
                    </Stack>

                    <LinearProgress
                      variant="determinate"
                      value={warehousePct}
                      color={stockStatus}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  {isLowStock && (
                    <Chip
                      icon={<WarningAmberIcon />}
                      label="Bajo stock"
                      size="small"
                      color="error"
                    />
                  )}
                </Stack>
              </Box>

              {/* ACCIONES */}
              <Stack direction="row" spacing={1}>
                {currentTab !== 2 && (
                  <Tooltip title="Transferir stock">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        onTransfer(product);
                      }}
                      color="primary"
                    >
                      <SwapHorizIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Editar">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(product);
                    }}
                    color="info"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>

                {currentTab === 0 && (
                <Tooltip title="Eliminar">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(product);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
                )}
              </Stack>
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default ProductListView;
